import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { getSupabaseClient, TypedSupabaseClient } from '../lib/supabaseClient';

// Create context with undefined default value
const SupabaseContext = createContext<TypedSupabaseClient | undefined>(undefined);

type SupabaseProviderProps = {
  children: ReactNode;
};

/**
 * Provider component that creates a memoized Supabase client instance
 * and makes it available to all child components via context
 */
export function SupabaseProvider({ children }: SupabaseProviderProps) {
  // Create a memoized instance of the Supabase client
  const supabase = useMemo(() => getSupabaseClient(), []);

  return (
    <SupabaseContext.Provider value={supabase}>
      {children}
    </SupabaseContext.Provider>
  );
}

/**
 * Custom hook to access the Supabase client from any component
 * @returns The Supabase client instance
 * @throws Error if used outside of SupabaseProvider
 */
export function useSupabase(): TypedSupabaseClient {
  const context = useContext(SupabaseContext);
  
  if (context === undefined) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  
  return context;
}
